// src/components/PropertyPanel/PropertyPanelContext.js
import React, { createContext, useState, useContext, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateComponent, selectSelectedComponent } from '../../redux/editorSlice';
import { handleImageUpload as uploadImage } from './utils/propertyUtils';
import { COMPONENT_TYPES } from '../../constants';
// 새로 추가된 유틸리티 함수 불러오기
import { handleImageChange, handleDataChange, handleContentChange as contentChange } from './utils/contentUtils';
import { 
  ensureDeviceStylesApplied, 
  applyBoardStylesWithFallback,
  loadDeviceSpecificProperties 
} from './utils/deviceUtils';

const PropertyPanelContext = createContext();

export const PropertyPanelProvider = ({ children }) => {
  const dispatch = useDispatch();
  const selectedComponent = useSelector(selectSelectedComponent);
  
  // 패널 UI 상태
  const [activeTab, setActiveTab] = useState('layout');
  const [devicePropertyTab, setDevicePropertyTab] = useState('pc');
  const [boardDeviceTab, setBoardDeviceTab] = useState(() => {
    return window.innerWidth <= 768 ? 'mobile' : 'pc';
  });
  const [focusedProps, setFocusedProps] = useState({});
  
  // 패딩 입력값 관리를 위한 로컬 상태 추가
  const [paddingInputs, setPaddingInputs] = useState({
    top: "",
    right: "",
    bottom: "",
    left: ""
  });
  
  // 테두리 둥글기 입력값 관리를 위한 로컬 상태 추가
  const [borderRadiusInput, setBorderRadiusInput] = useState("");
  
  // 리치 에디터 상태
  const [isRichEditorOpen, setIsRichEditorOpen] = useState(false);
  const [richEditorContent, setRichEditorContent] = useState('');
  
  // selectedComponent는 Redux에서 가져오므로 여기서는 필요 없음
  // 대신 값을 전달받아 사용
  
  // 디바이스 탭 변경 핸들러
  const handleDevicePropertyTabChange = (newDeviceTab) => {
    setDevicePropertyTab(newDeviceTab);
  };
  
  // 패널 탭 변경 핸들러
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  
  // 스타일 변경 핸들러
  const handleStyleChange = (selectedComponent, property, value) => {
    if (!selectedComponent) return;
    
    const existingStyle = JSON.parse(JSON.stringify(selectedComponent.style || {}));
    existingStyle[property] = value;
    
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        style: existingStyle
      }
    }));
  };
  
  // 컴포넌트 속성 변경 핸들러
  const handleComponentPropsChange = (property, value) => {
    if (!selectedComponent) return;
    
    // 디바이스 속성 탭이 선택된 경우, 해당 디바이스에 대한 componentProps만 변경
    if (devicePropertyTab) {
      // 깊은 복사를 위해 JSON.parse(JSON.stringify()) 사용
      const existingProps = selectedComponent.componentProps ? 
        JSON.parse(JSON.stringify(selectedComponent.componentProps)) : 
        {};
      
      // 디바이스별 객체 초기화
      if (!existingProps.deviceProperty) {
        existingProps.deviceProperty = {
          pc: {},
          mobile: {}
        };
        
        // 기존 속성을 PC/모바일에 복사 (deviceProperty 제외)
        Object.keys(existingProps).forEach(key => {
          if (key !== 'deviceProperty') {
            existingProps.deviceProperty.pc[key] = existingProps[key];
            existingProps.deviceProperty.mobile[key] = existingProps[key];
          }
        });
      }
      
      // 선택된 디바이스 탭에 속성 업데이트
      if (!existingProps.deviceProperty[devicePropertyTab]) {
        existingProps.deviceProperty[devicePropertyTab] = {};
      }
      
      existingProps.deviceProperty[devicePropertyTab][property] = value;
      
      // PC 모드일 때만 최상위 속성에도 적용
      if (devicePropertyTab === 'pc') {
        existingProps[property] = value;
      }
      
      dispatch(updateComponent({
        id: selectedComponent.id,
        changes: {
          componentProps: existingProps
        }
      }));
    } else {
      // 기존 로직을 수정하여 디바이스별 속성도 업데이트
      const existingProps = JSON.parse(JSON.stringify(selectedComponent.componentProps || {}));
      
      // 최상위 속성 업데이트
      existingProps[property] = value;
      
      // 디바이스별 속성도 업데이트
      if (existingProps.deviceProperty) {
        if (existingProps.deviceProperty.pc) {
          existingProps.deviceProperty.pc[property] = value;
        }
        if (existingProps.deviceProperty.mobile) {
          existingProps.deviceProperty.mobile[property] = value;
        }
      }
      
      dispatch(updateComponent({
        id: selectedComponent.id,
        changes: {
          componentProps: existingProps
        }
      }));
    }
  };

  // 컨텐츠 변경 핸들러
  const handleContentChange = (selectedComponent, value) => {
    if (!selectedComponent) return;
    
    contentChange(selectedComponent, value, devicePropertyTab, dispatch);
  };

  // 리치 에디터 저장 핸들러 - selectedComponent 매개변수 제거
  const handleRichEditorSave = (content) => {
    if (!selectedComponent) return;
    
    // content가 문자열인지 확인
    if (typeof content !== 'string') {
      console.error('저장 실패: 콘텐츠가 문자열이 아닙니다', content);
      return;
    }
    
    // RICH_EDITOR 컴포넌트일 경우 간단히 처리
    if (selectedComponent.type === COMPONENT_TYPES.RICH_EDITOR) {
      dispatch(updateComponent({
        id: selectedComponent.id,
        changes: {
          content: content
        }
      }));
    } 
    // 다른 컴포넌트(이미지-텍스트 등)는 기존 방식대로 처리
    else if (devicePropertyTab) {
      const existingContent = selectedComponent.content ? 
        JSON.parse(JSON.stringify(selectedComponent.content)) : 
        { deviceProperty: { pc: '', mobile: '' } };
      
      // deviceProperty가 없으면 초기화
      if (!existingContent.deviceProperty) {
        existingContent.deviceProperty = { pc: '', mobile: '' };
      }
      
      // 현재 디바이스 탭에 내용 저장 (문자열로 저장)
      existingContent.deviceProperty[devicePropertyTab] = content;
      
      // PC일 경우 최상위에도 저장
      if (devicePropertyTab === 'pc') {
        existingContent.html = content;
      }
      
      dispatch(updateComponent({
        id: selectedComponent.id,
        changes: {
          content: existingContent
        }
      }));
    } else {
      // 일반 모드일 경우 직접 content에 저장
      dispatch(updateComponent({
        id: selectedComponent.id,
        changes: {
          content: content
        }
      }));
    }
    
    // 모달 닫기
    setIsRichEditorOpen(false);
  };

  // 디바이스별 속성값 가져오기
  const getDevicePropertyValue = (source, field, defaultValue) => {
    if (!source || !field) return defaultValue;
    
    try {
      // 디바이스별 속성이 있는 경우
      if (devicePropertyTab && 
          source.deviceProperty && 
          source.deviceProperty[devicePropertyTab] && 
          source.deviceProperty[devicePropertyTab][field] !== undefined) {
        return source.deviceProperty[devicePropertyTab][field];
      }
      
      // 일반 속성 필드
      if (source[field] !== undefined) {
        return source[field];
      }
      
      return defaultValue;
    } catch (error) {
      console.error('getDevicePropertyValue error:', error);
      return defaultValue;
    }
  };

  // 이미지 업로드 핸들러
  const handleImageUpload = async (e) => {
    return await uploadImage(e);
  };

  // 리치 에디터 열기
  const openRichEditor = (initialContent) => {
    setRichEditorContent(initialContent || '');
    setIsRichEditorOpen(true);
  };
  
  // 새로 추가된 함수들
  
  // 디바이스별 스타일 강제 적용
  const applyDeviceStyles = (forceApply = false) => {
    if (selectedComponent) {
      dispatch(ensureDeviceStylesApplied(selectedComponent, boardDeviceTab, forceApply));
    }
  };
  
  // 이미지 변경 핸들러
  const handleImageContentChange = (field, value) => {
    if (selectedComponent) {
      handleImageChange(selectedComponent, field, value, dispatch);
    }
  };
  
  // 데이터 변경 핸들러
  const handleComponentDataChange = (field, value) => {
    if (selectedComponent) {
      handleDataChange(selectedComponent, field, value, devicePropertyTab, dispatch);
    }
  };
  
  // loadDeviceSpecificProperties 래퍼 함수
  const loadCurrentDeviceProperties = (deviceType, paddingSetter, borderRadiusSetter) => {
    if (selectedComponent) {
      loadDeviceSpecificProperties(
        selectedComponent, 
        deviceType || devicePropertyTab, 
        paddingSetter || setPaddingInputs, 
        borderRadiusSetter || setBorderRadiusInput
      );
    }
  };
  
  const value = {
    // 패널 UI 상태
    activeTab,
    setActiveTab,
    devicePropertyTab,
    setDevicePropertyTab,
    boardDeviceTab,
    setBoardDeviceTab,
    focusedProps,
    setFocusedProps,
    handleDevicePropertyTabChange,
    handleTabChange,
    
    // 패딩 및 테두리 입력값 상태
    paddingInputs,
    setPaddingInputs,
    borderRadiusInput,
    setBorderRadiusInput,
    
    // 컴포넌트 속성 관련
    handleStyleChange,
    handleComponentPropsChange,
    handleContentChange,
    handleRichEditorSave,
    handleImageUpload,
    getDevicePropertyValue,
    
    // 리치 에디터 관련
    isRichEditorOpen,
    setIsRichEditorOpen,
    richEditorContent,
    setRichEditorContent,
    openRichEditor,
    
    // 새로 추가된 기능들
    applyDeviceStyles,
    handleImageContentChange,
    handleComponentDataChange,
    loadCurrentDeviceProperties
  };
  
  return (
    <PropertyPanelContext.Provider value={value}>
      {children}
    </PropertyPanelContext.Provider>
  );
};

export const usePropertyPanel = () => useContext(PropertyPanelContext);